import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';

import { Btn } from '../../../../AbstractElements';
import Select from 'react-select';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../../../config';

import TenantAuditSummary from './TenantAuditSummary';
import Widgets1 from '../../../Common/CommonWidgets/Widgets1';

// Render dropdown menus above the stat widgets below the filter card
const selectMenuStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

// Property dropdown value for "All" accessible properties
const ALL = 'ALL';

const AuditDashboard = () => {

  const concessionaire_id = localStorage.getItem('concessionaireId');
  const property_access = localStorage.getItem('propertyAccess');
  const token = localStorage.getItem('sessiontoken');
  const login_id = localStorage.getItem('loginId');
  const login_name = localStorage.getItem('loginName');
  const TOLERANCE = Number(localStorage.getItem('auditTolerance') || 0);
  const individualEnabled = localStorage.getItem('auditIndividualTenants') === 'true';

  const navigate = useNavigate();
  const location = useLocation();

  const configheader = {
    headers: { Authorization: token }
  };

  const [properties, setProperties] = useState([]);
  const [property_id, setPropertyId] = useState(ALL);
  const [propertyName, setPropertyName] = useState('All Properties');

  const [auditPeriodId, setAuditPeriodId] = useState(null);
  const [tenantAudits, setTenantAudits] = useState([]);

  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState(null);
  const [propertyError, setPropertyError] = useState('');
  const [monthError, setMonthError] = useState('');

  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenantError, setTenantError] = useState('');

  const emptyIndividualValues = { gross_sales: '', net_sales: '', tax: '', discount: '', returns: '' };
  const [individualModal, setIndividualModal] = useState(false);
  const [individualValues, setIndividualValues] = useState(emptyIndividualValues);
  const [individualError, setIndividualError] = useState('');
  const [individualSubmitting, setIndividualSubmitting] = useState(false);

  const getPreviousMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getPreviousMonth());

  const getMonth = () => Number(selectedMonth.split("-")[1]);
  const getYear = () => Number(selectedMonth.split("-")[0]);

  const formatMonthYear = (ym) => {
    const [year, month] = ym.split("-");
    return new Date(year, month - 1).toLocaleString("en-US", {
      month: "long",
      year: "numeric"
    });
  };

  /* -------------------- PROPERTY OPTIONS -------------------- */

  const propertyOptions = useMemo(
    () => {
      const options = properties.map(p => ({
        value: p._id,
        label: `${p.property_name} (${p.property_mall_code})`
      }));
      return options.length > 0 ? [{ value: ALL, label: 'All' }, ...options] : options;
    },
    [properties]
  );

  const isAll = property_id === ALL;
  const accessiblePropertyIds = properties.map(p => p._id);

  /* -------------------- TENANT OPTIONS -------------------- */

  const tenantOptions = useMemo(
    () =>
      tenants.map(t => ({
        value: t._id,
        label: isAll && t.tenant_property_id?.property_name
          ? `${t.tenant_store_name} (${t.tenant_code}) – ${t.tenant_property_id.property_name}`
          : `${t.tenant_store_name} (${t.tenant_code})`
      })),
    [tenants, isAll]
  );

  const selectedTenant = tenants.find(t => t._id === selectedTenantId);

  // tenant_property_id is populated by the tenant endpoint
  const tenantPropertyId = selectedTenant?.tenant_property_id?._id || selectedTenant?.tenant_property_id;
  const tenantPropertyName = selectedTenant?.tenant_property_id?.property_name || propertyName;

  /* -------------------- STATS -------------------- */

  const stats = useMemo(() => {

    const result = {
      total: tenantAudits.length,
      matched: 0,
      notMatched: 0,
      reconciled: 0,
      pending: 0
    };

    tenantAudits.forEach(t => {
      if (t.status === "MATCHED") result.matched++;
      if (t.status === "NOT_MATCHED") result.notMatched++;
      if (t.status === "RECONCILED") result.reconciled++;
      if (t.status === "PENDING") result.pending++;
    });

    return result;

  }, [tenantAudits]);

  /* -------------------- RESTORE STATE FROM UPLOAD PAGE -------------------- */

  useEffect(() => {

    if (!location.state) return;

    const { property_id, property_name, year, month } = location.state;

    if (property_id) {

      setPropertyId(property_id);
      setPropertyName(property_name);

      const m = String(month).padStart(2, "0");
      setSelectedMonth(`${year}-${m}`);

    }

  }, [location.state]);

  /* -------------------- LOAD PROPERTIES -------------------- */

  useEffect(() => {

    axios
      .post(
        `${config.hostname}/property/getpropertybyconcessionaire`,
        {
          property_concessionaire_id: concessionaire_id,
          property_id: property_access
        },
        configheader
      )
      .then(res => setProperties(res.data))
      .catch(console.error);

  }, []);

  /* -------------------- FETCH AUDIT DATA -------------------- */

  const fetchAuditData = () => {

    if (!property_id || (isAll && accessiblePropertyIds.length === 0)) {
      setTenantAudits([]);
      setAuditPeriodId(null);
      return;
    }

    setLoading(true);

    axios
      .post(
        `${config.hostname}/audit/getauditperiod`,
        {
          concessionaire_id,
          property_id,
          ...(isAll && { property_ids: accessiblePropertyIds }),
          month: getMonth(),
          year: getYear()
        },
        configheader
      )
      .then(res => {

        setAuditPeriodId(res.data.audit_period_id || null);
        setTenantAudits(res.data.tenantAudits || []);
        setIsLocked(res.data.is_locked);

      })
      .catch(() => {
        setTenantAudits([]);
        setAuditPeriodId(null);
      })
      .finally(() => setLoading(false));
  };

  // properties: "All" needs the loaded property list (also when restored from another page)
  useEffect(() => {
    fetchAuditData();
  }, [property_id, selectedMonth, properties]);

  /* -------------------- LOAD TENANTS OF PROPERTY -------------------- */

  useEffect(() => {

    setTenants([]);
    setSelectedTenantId('');

    if (!individualEnabled || !property_id) return;
    if (isAll && accessiblePropertyIds.length === 0) return;

    axios
      .post(
        `${config.hostname}/tenant/getenantsbyconcessionaire`,
        {
          tenant_concessionaire_id: concessionaire_id,
          property_id: isAll ? accessiblePropertyIds.join(',') : property_id
        },
        configheader
      )
      .then(res => setTenants(res.data || []))
      .catch(console.error);

  }, [property_id, properties]);

  /* -------------------- HANDLERS -------------------- */

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleUploadNav = () => {

    let valid = true;

    // Bulk upload is per property, so "All" is not allowed
    if (!property_id) {
      setPropertyError("Select a property");
      valid = false;
    } else if (isAll) {
      setPropertyError("Select a specific property for bulk upload");
      valid = false;
    } else {
      setPropertyError("");
    }

    if (!selectedMonth) {
      setMonthError("Select a month");
      valid = false;
    } else {
      setMonthError("");
    }

    if (!valid) return;

    navigate(`${process.env.PUBLIC_URL}/admin/audit/uploadmonthlysales/default`, {
      state: {
        property_id,
        property_name: propertyName,
        month: getMonth(),
        year: getYear(),
        audit_period_id: auditPeriodId
      }
    });

  };

  const handleIndividualOpen = () => {

    let valid = true;

    if (!property_id) {
      setPropertyError("Select a property");
      valid = false;
    } else {
      setPropertyError("");
    }

    if (!selectedMonth) {
      setMonthError("Select a month");
      valid = false;
    } else {
      setMonthError("");
    }

    if (!selectedTenantId) {
      setTenantError("Select a tenant");
      valid = false;
    } else {
      setTenantError("");
    }

    if (!valid) return;

    setIndividualValues(emptyIndividualValues);
    setIndividualError('');
    setIndividualModal(true);
  };

  const closeIndividualModal = () => {
    if (individualSubmitting) return;
    setIndividualModal(false);
  };

  const insertToLog = (task) => {
    axios
      .post(
        `${config.hostname}/log`,
        {
          created_by: login_id,
          concessionaire_id,
          module: "Audit",
          task: `${login_name} ${task}`
        },
        configheader
      )
      .catch(console.error);
  };

  const handleIndividualSubmit = () => {

    const netSales = String(individualValues.net_sales).trim();

    if (netSales === '' || isNaN(Number(netSales))) {
      setIndividualError("Net sales is required");
      return;
    }

    // Optional fields left blank are not sent, so they are not compared
    const UPLOADED = { net_sales: Number(netSales) };

    for (const field of ['gross_sales', 'tax', 'discount', 'returns']) {
      const value = String(individualValues[field]).trim();
      if (value === '') continue;
      if (isNaN(Number(value))) {
        setIndividualError("Sales values must contain valid numbers only.");
        return;
      }
      UPLOADED[field] = Number(value);
    }

    setIndividualError('');
    setIndividualSubmitting(true);

    axios
      .post(
        `${config.hostname}/audit/createtenantauditindividual`,
        {
          // With "All", the backend finds or creates the tenant's own property period
          audit_period_id: isAll ? null : auditPeriodId,
          concessionaire_id,
          property_id: isAll ? tenantPropertyId : property_id,
          month: getMonth(),
          year: getYear(),
          login_id,
          tolerance: TOLERANCE,
          data: [{
            mall_code: selectedTenant.tenant_mall_code,
            tenant_code: selectedTenant.tenant_code,
            outlet_code: selectedTenant.tenant_unit_code,
            UPLOADED
          }]
        },
        configheader
      )
      .then(res => {

        const failed = res.data.failed_rows?.[0];

        if (failed) {
          toast.error(failed.reason);
        } else if (res.data.upload_summary?.matched) {
          toast.success("Sales uploaded - Matched");
        } else if (res.data.upload_summary?.not_matched) {
          toast.warning("Sales uploaded - Not Matched");
        } else {
          toast.error(res.data.message || "No sales found for this tenant in the selected month");
        }

        if (!failed) {
          insertToLog(`Individual sales entered for ${selectedTenant.tenant_store_name} (${selectedTenant.tenant_code}) - ${tenantPropertyName} ${formatMonthYear(selectedMonth)}`);
        }

        setIndividualModal(false);
        fetchAuditData();
      })
      .catch(err => {
        setIndividualError(err.response?.data?.message || 'Upload failed.');
      })
      .finally(() => setIndividualSubmitting(false));
  };

  const handleStatClick = status => {
    setActiveStatusFilter(prev => (prev === status ? null : status));
  };

  /* -------------------- UI -------------------- */

  return (

    <Fragment>

      <Container fluid>

        {/* FILTER CARD */}

        <Card>

          <CardBody>

            <Row className="align-items-end">

              <Col md={individualEnabled ? "3" : "4"}>

                <FormGroup style={{ position: "relative" }}>

                  <Label>Property</Label>

                  <Select
                    placeholder="Select Property"
                    isClearable
                    value={propertyOptions.find(o => o.value === property_id) || null}
                    options={propertyOptions}
                    menuPortalTarget={document.body}
                    styles={selectMenuStyles}
                    onChange={opt => {

                      const id = opt?.value || "";
                      setPropertyId(id);

                      const selected = properties.find(p => p._id === id);
                      setPropertyName(id === ALL ? "All Properties" : selected?.property_name || "");

                      setActiveStatusFilter(null);
                      setPropertyError("");

                    }}
                  />
                  {propertyError && (
                    <div style={{ position: "absolute", top: "70px", left: "0", color: "red", fontSize: "12px" }} >
                      {propertyError}
                    </div>
                  )}
                </FormGroup>

              </Col>

              <Col md={individualEnabled ? "2" : "4"}>
                <FormGroup style={{ position: "relative" }}>
                  <Label>Month</Label>
                  <input
                    className="form-control"
                    type="month"
                    value={selectedMonth}
                    max={getPreviousMonth()}
                    onChange={(e) => {
                      setSelectedMonth(e.target.value);
                      setMonthError("");
                    }}
                  />

                  {monthError && (
                    <div style={{ position: "absolute", top: "70px", left: "0", color: "red", fontSize: "12px" }} >
                      {monthError}
                    </div>
                  )}

                </FormGroup>
              </Col>

              {individualEnabled && (
                <Col md="3">
                  <FormGroup style={{ position: "relative" }}>
                    <Label>Tenant</Label>
                    <Select
                      placeholder="Select Tenant"
                      isClearable
                      isDisabled={!property_id}
                      value={tenantOptions.find(o => o.value === selectedTenantId) || null}
                      options={tenantOptions}
                      menuPortalTarget={document.body}
                      styles={selectMenuStyles}
                      onChange={opt => {
                        setSelectedTenantId(opt?.value || "");
                        setTenantError("");
                      }}
                    />
                    {tenantError && (
                      <div style={{ position: "absolute", top: "70px", left: "0", color: "red", fontSize: "12px" }} >
                        {tenantError}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              )}

              <Col md={individualEnabled ? "4" : undefined} className="ms-auto">

                <FormGroup>

                  <Label>&nbsp;</Label>

                  <div className="d-flex flex-wrap justify-content-end gap-2">

                    {individualEnabled && (
                      <Btn
                        attrBtn={{
                          color: 'primary',
                          className: 'text-nowrap',
                          onClick: handleIndividualOpen
                        }}
                      >
                        Upload Sales-Individual
                      </Btn>
                    )}

                    <Btn
                      attrBtn={{
                        color: 'primary',
                        className: 'text-nowrap',
                        onClick: handleUploadNav
                      }}
                    >
                      Upload Sales-Bulk
                    </Btn>

                  </div>

                </FormGroup>

              </Col>

            </Row>

          </CardBody>

        </Card>

        {/* KPI WIDGETS */}

        <Card>

          <Row className="mt-3 g-3">

            <Col>
              <Widgets1 data={{ title: 'Total Tenants', total: stats.total, color: 'primary', icon: 'customers' }} />
            </Col>

            <Col>
              <div onClick={() => handleStatClick('MATCHED')}>
                <Widgets1 data={{ title: 'Matched', total: stats.matched, color: 'success', icon: 'customers' }} />
              </div>
            </Col>

            <Col>
              <div onClick={() => handleStatClick('NOT_MATCHED')}>
                <Widgets1 data={{ title: 'Not Matched', total: stats.notMatched, color: 'secondary', icon: 'customers' }} />
              </div>
            </Col>

            <Col>
              <div onClick={() => handleStatClick('RECONCILED')}>
                <Widgets1 data={{ title: 'Reconciled', total: stats.reconciled, color: 'warning', icon: 'customers' }} />
              </div>
            </Col>

            <Col>
              <div onClick={() => handleStatClick('PENDING')}>
                <Widgets1 data={{ title: 'Pending', total: stats.pending, color: 'info', icon: 'customers' }} />
              </div>
            </Col>

          </Row>

        </Card>

        {/* SUMMARY TABLE */}

        <Card className="mt-3">

          <CardHeader>
            <h5>Tenant Audit Summary – {formatMonthYear(selectedMonth)}</h5>
          </CardHeader>

          <CardBody>

            <TenantAuditSummary
              tenantAudits={tenantAudits}
              loading={loading}
              propertySelected={!!property_id}
              monthLabel={getMonth()}
              selectedYear={getYear()}
              isLocked={isLocked}
              auditPeriodId={auditPeriodId}
              activeStatusFilter={activeStatusFilter}
              setActiveStatusFilter={setActiveStatusFilter}
              onRefresh={fetchAuditData}
              property_id={property_id}
              propertyName={propertyName}
            />

          </CardBody>

        </Card>

      </Container>

      {/* INDIVIDUAL SALES MODAL */}

      <Modal
        className="modal-dialog-centered"
        isOpen={individualModal}
        toggle={closeIndividualModal}
        backdrop="static"
      >

        <ModalHeader toggle={closeIndividualModal}>
          Upload Sales – {selectedTenant ? `${selectedTenant.tenant_store_name} (${selectedTenant.tenant_code})` : ''} – {formatMonthYear(selectedMonth)}
        </ModalHeader>

        <ModalBody>

          {[
            { field: 'gross_sales', label: 'Gross Sales' },
            { field: 'net_sales', label: 'Net Sales', required: true },
            { field: 'tax', label: 'Tax' },
            { field: 'discount', label: 'Discount' },
            { field: 'returns', label: 'Return Amount' }
          ].map(({ field, label, required }) => (
            <FormGroup key={field}>
              <Label>
                {label}
                {required && <span style={{ color: 'red' }}> *</span>}
              </Label>
              <input
                className="form-control"
                type="number"
                step="any"
                onWheel={(e) => e.target.blur()}
                placeholder={label}
                value={individualValues[field]}
                onChange={(e) => {
                  setIndividualValues(prev => ({ ...prev, [field]: e.target.value }));
                  setIndividualError('');
                }}
              />
            </FormGroup>
          ))}

          {individualError && <small className="text-danger d-block">{individualError}</small>}

        </ModalBody>

        <ModalFooter>

          <Btn attrBtn={{ color: 'secondary', onClick: closeIndividualModal, disabled: individualSubmitting }}>
            Cancel
          </Btn>

          <Btn attrBtn={{ color: 'primary', onClick: handleIndividualSubmit, disabled: individualSubmitting }}>
            OK
          </Btn>

        </ModalFooter>

      </Modal>

    </Fragment>
  );
};

export default AuditDashboard;